import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowRight } from "lucide-react";

const inp = "w-full bg-black border border-white/15 focus:border-[#D4AF37] outline-none px-3 py-2.5 rounded text-sm text-white";

export default function ChurchBuilderRegister() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const selectedPlan = params.get("plan") || "starter";
  const selectedInterval = params.get("interval") || "monthly";
  const isPaid = ["starter", "standard", "premium"].includes(selectedPlan);

  const [form, setForm] = useState({
    name: "",
    denomination: "",
    country: "United Kingdom",
    address: "",
    phone: "",
    email: "",
    website: "",
    pastor_name: "",
    template_id: params.get("template") || "traditional",
  });
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      const back = `/church-builder/register?plan=${selectedPlan}&interval=${selectedInterval}`;
      navigate(`/login?next=${encodeURIComponent(back)}`);
      return;
    }
    api.get("/church-builder/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, [user, authLoading, navigate, selectedPlan, selectedInterval]);

  const planDef = useMemo(() => plans.find((p) => p.id === selectedPlan), [plans, selectedPlan]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Church name is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/church-builder/register", {
        ...form,
        subscription_plan: isPaid ? "trial" : "trial", // server starts everyone on trial until Stripe confirms
      });
      toast.success(`${data.name} created`);
      if (isPaid) {
        // Move on to payment details collection — Stripe Checkout in subscription mode
        navigate(`/church-builder/${data.slug}/subscribe?plan=${selectedPlan}&interval=${selectedInterval}`);
      } else {
        navigate(`/church-builder/${data.slug}/dashboard`);
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string"
        ? d
        : Array.isArray(d)
          ? d.map((e) => `${(e.loc || []).slice(-1)[0]}: ${e.msg}`).join("; ")
          : "Could not create church";
      toast.error(msg);
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="cb-register" className="bg-[#0A0A0A] text-white min-h-screen">
      <div className="max-w-2xl mx-auto px-6 py-16">
        <div className="label-eyebrow mb-3">Get started</div>
        <h1 className="font-serif-display text-5xl">Register your <span className="italic text-[#D4AF37]">church</span></h1>
        <p className="text-neutral-300 mt-4">7-day free trial. Card details collected upfront — billing starts only after the trial ends.</p>

        {planDef && (
          <div data-testid="cb-reg-plan-summary" className="mt-6 rounded-md border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-4 py-3 text-sm">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="text-[10px] tracking-widest uppercase text-[#D4AF37]">Selected plan</div>
                <div className="font-serif-display text-xl mt-1" data-testid="cb-reg-plan-name">{planDef.name}</div>
              </div>
              <div className="text-right">
                <div className="text-[10px] tracking-widest uppercase text-neutral-400">{selectedInterval}</div>
                <div className="font-serif-display text-xl text-[#D4AF37]" data-testid="cb-reg-plan-price">£{selectedInterval === "annual" ? planDef.price_annual : planDef.price_monthly}<span className="text-xs text-neutral-500">{selectedInterval === "annual" ? "/yr" : "/mo"}</span></div>
              </div>
            </div>
            <div className="mt-1 text-xs text-neutral-400">After signup you'll enter card details via Stripe. Your card is charged only after the 7-day trial.</div>
          </div>
        )}

        <form onSubmit={submit} className="mt-10 space-y-5">
          <Field label="Church Name *">
            <input data-testid="cb-reg-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inp} placeholder="CAC Outreach HQ" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Denomination">
              <input data-testid="cb-reg-denomination" value={form.denomination} onChange={(e) => setForm({ ...form, denomination: e.target.value })} className={inp} placeholder="Christ Apostolic Church" />
            </Field>
            <Field label="Country">
              <input data-testid="cb-reg-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className={inp} />
            </Field>
          </div>
          <Field label="Address">
            <input data-testid="cb-reg-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inp} placeholder="1 Church Street, London EC1" />
          </Field>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Phone">
              <input data-testid="cb-reg-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inp} />
            </Field>
            <Field label="Church Email">
              <input data-testid="cb-reg-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inp} placeholder="hello@church.org" />
            </Field>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Pastor Name">
              <input data-testid="cb-reg-pastor" value={form.pastor_name} onChange={(e) => setForm({ ...form, pastor_name: e.target.value })} className={inp} />
            </Field>
            <Field label="Existing Website">
              <input data-testid="cb-reg-website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className={inp} placeholder="https://..." />
            </Field>
          </div>
          <Field label="Starting Template">
            <select data-testid="cb-reg-template" value={form.template_id} onChange={(e) => setForm({ ...form, template_id: e.target.value })} className={inp}>
              <option value="traditional">Traditional Church (available now)</option>
              <option value="contemporary" disabled>Contemporary (coming soon)</option>
              <option value="outreach" disabled>Outreach Ministry (coming soon)</option>
            </select>
          </Field>
          <p className="text-[11px] text-neutral-500">Logo, hero image, pastor photo and gallery images are uploaded from your device once your dashboard opens.</p>

          <div className="flex items-center justify-between pt-4 border-t border-white/10">
            <Link to="/church-builder/pricing" className="text-xs tracking-widest uppercase text-neutral-400">← Change plan</Link>
            <button data-testid="cb-reg-submit" type="submit" disabled={saving} className="btn-gold px-6 py-3 rounded text-xs tracking-widest uppercase font-semibold inline-flex items-center gap-2">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
              {isPaid ? "Continue to payment" : "Create Church"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] tracking-widest uppercase text-neutral-400">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
